import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-card.css';

export type ExtCardElevation = 'flat' | 'sm' | 'md';
export type ExtCardPadding = 'sm' | 'md' | 'lg' | 'none';

/**
 * <ext-card clickable elevation="sm" padding="md">
 *   <div slot="header">Titulo</div>
 *   Contenido principal
 *   <div slot="footer">Pie</div>
 * </ext-card>
 *
 * Container con border-radius, shadow y slots named header/footer.
 *
 * Light DOM: no hay <slot>. Convencion: el consumidor marca los children
 * con attribute `slot="header"` o `slot="footer"`. En connectedCallback
 * (y cuando hayan mutaciones agregadas por el usuario) clasificamos los
 * children en tres buckets y los envolvemos en wrappers:
 *   .ext-card__header -> slot=header
 *   .ext-card__body   -> resto
 *   .ext-card__footer -> slot=footer
 *
 * Eventos:
 *  - ext:click (solo si clickable=true) - detail vacio.
 *
 * Props:
 *  - clickable: boolean (default false).
 *  - elevation: flat | sm | md (default sm).
 *  - padding:   sm | md | lg | none (default md).
 */
@customElement('ext-card')
export class ExtCard extends ExtBaseElement {
  @property({ type: Boolean, reflect: true }) clickable = false;
  @property({ type: String, reflect: true }) elevation: ExtCardElevation = 'sm';
  @property({ type: String, reflect: true }) padding: ExtCardPadding = 'md';

  private _structured = false;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  render(): null {
    return null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._structure();
    if (this.clickable) this._enableClickable();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('clickable')) {
      if (this.clickable) this._enableClickable();
      else this._disableClickable();
    }
  }

  private _enableClickable(): void {
    this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
    this.addEventListener('click', this._onClick);
    this.addEventListener('keydown', this._onKey);
  }

  private _disableClickable(): void {
    this.removeAttribute('role');
    this.removeAttribute('tabindex');
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keydown', this._onKey);
  }

  private _onClick = (_ev: MouseEvent): void => {
    this._emit('click');
  };

  private _onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      this._emit('click');
    }
  };

  /**
   * Reordena los children originales en tres wrappers: header / body / footer.
   * Solo corre una vez (idempotente): si ya envolvimos, salimos.
   * Esto soporta el patron t-out / textContent de Odoo donde el servidor
   * ya dejo los children en el DOM antes de que Lit upgrade el element.
   */
  private _structure(): void {
    if (this._structured) return;
    const children = Array.from(this.childNodes);
    const headerNodes: Node[] = [];
    const bodyNodes: Node[] = [];
    const footerNodes: Node[] = [];

    for (const node of children) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const slot = (node as Element).getAttribute('slot');
        if (slot === 'header') {
          headerNodes.push(node);
          continue;
        }
        if (slot === 'footer') {
          footerNodes.push(node);
          continue;
        }
      }
      bodyNodes.push(node);
    }

    // Si no hay ningun content (puede pasar en pruebas), igual estructuramos
    // para que los estilos del body apliquen.
    this.innerHTML = '';

    if (headerNodes.length > 0) {
      const h = document.createElement('div');
      h.className = 'ext-card__header';
      headerNodes.forEach((n) => h.appendChild(n));
      this.appendChild(h);
    }

    const b = document.createElement('div');
    b.className = 'ext-card__body';
    bodyNodes.forEach((n) => b.appendChild(n));
    this.appendChild(b);

    if (footerNodes.length > 0) {
      const f = document.createElement('div');
      f.className = 'ext-card__footer';
      footerNodes.forEach((n) => f.appendChild(n));
      this.appendChild(f);
    }

    this._structured = true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-card': ExtCard;
  }
}
